// Solarpunk skin: a cyclist rides a cargo bike down the greenway to a solar greenhouse.
//   idle    - bike stopped by the greenhouse, a foot down; butterflies flit by the dome
//   pending - rider pedals the greenway; progress eases them toward the dome
//   failure - rider brakes and a smog cloud blocks the path
//   success - the greenhouse glass lights green, the path greens and pollen drifts
import { makeScene, mix, type Palette, type SceneCtx } from '../scene/engine';
import type { SkinDef } from './types';

const colors = (g: (n: string) => string): Palette => {
  const sky = g('--bg-raise'), city = mix(sky, g('--line-bright'), 0.45), post = mix(g('--line-bright'), g('--ink'), 0.3);
  return {
    skyTop: mix(sky, g('--violet'), 0.12), skyBot: mix(sky, g('--amber'), 0.14),
    ground: g('--surface'), groundLo: g('--bg'), edge: g('--line-bright'), dash: g('--line'),
    accent: g('--accent'), green: g('--green'), red: g('--red'), amber: g('--amber'), gild: g('--gild'), violet: g('--violet'),
    sun: '#ffe08a', sunGlow: '#ffc24a',
    city, roof: mix(g('--green'), city, 0.62), window: mix(g('--accent'), city, 0.6),
    turbine: mix(sky, g('--ink'), 0.5),
    post, panel: '#2f5f94', panelHi: '#7fb2e3', lamp: '#ffd75e', lampLo: mix('#ffd75e', post, 0.5),
    glass: mix(sky, '#bfe8dc', 0.35), glassHi: mix(sky, '#ffffff', 0.5), brick: mix(g('--red'), g('--surface'), 0.4),
    leaf: g('--green'), leafHi: mix(g('--green'), '#ffffff', 0.3), trunk: '#6b4a2a', seed: '#5a3a1a',
    skin: '#e9cfa0', hair: '#6b4424', hat: '#d9c48a', hatHi: '#efe0b0', shirt: '#f0c040', shirtHi: '#f8da7a', eye: '#2a211a',
    denim: '#4b6a93', denimLo: '#34506f', shoe: '#2a211a',
    // ink-derived so the tyres read on both grounds: pale at dusk, dark by day
    tyre: mix(g('--ink'), g('--bg'), 0.35), spoke: mix(g('--ink'), g('--bg'), 0.6), frame: g('--gild'), crank: '#50565a', saddle: '#3b2a1c',
    crate: '#9a6a3a', crateLo: '#6b4624', pot: '#c26a3e', soil: '#4a3222',
    soot: '#5d6066', sootHi: '#8a8d92', sootLo: '#44464b', speck: '#2a2b2e', smogEye: mix(g('--red'), '#3a3c41', 0.35),
    pollen: mix(g('--accent'), '#ffd75e', 0.5), pollen2: mix(g('--amber'), '#ffd75e', 0.35), bug: '#2a2622',
  };
};

// Filled pixel disc; translucent when `alpha` < 1 (sun halo, smog haze).
function disc(s: SceneCtx, cx: number, cy: number, r: number, c: string, alpha = 1) {
  for (let dy = -r; dy <= r; dy++) {
    const hw = Math.floor(Math.sqrt((r + 0.5) ** 2 - dy * dy));
    if (alpha < 1) s.glow(cx - hw, cy + dy, 2 * hw + 1, 1, c, alpha);
    else s.px(cx - hw, cy + dy, 2 * hw + 1, 1, c);
  }
}

// A low sun half-sunk behind the garden city, haloed in soft light.
function sun(s: SceneCtx, C: Palette) {
  const cx = 122, cy = 27;
  disc(s, cx, cy, 14, C.sunGlow, 0.08); disc(s, cx, cy, 9, C.sunGlow, 0.12);
  disc(s, cx, cy, 5, C.sun);
}

// One garden-city block: roof garden on top, a few lit windows, an optional trailing vine.
function block(s: SceneCtx, C: Palette, x: number, w: number, h: number, vine = -1) {
  const { px, groundY: GY } = s; const top = GY - h;
  px(x, top, w, h, C.city);
  px(x, top - 1, w, 2, C.roof); px(x + 1, top - 2, 2, 1, C.roof); px(x + w - 3, top - 2, 2, 1, C.roof);
  for (let wy = top + 3; wy < GY - 2; wy += 4) for (let wx = x + 2; wx < x + w - 2; wx += 4) px(wx, wy, 1, 1, C.window);
  if (vine >= 0) { px(x + vine, top + 1, 1, 5, C.roof); px(x + vine + 1, top + 3, 1, 1, C.roof); }
}
// Muted skyline: side-by-side blocks of different heights read as stepped terraces.
function skyline(s: SceneCtx, C: Palette) {
  block(s, C, 4, 12, 11); block(s, C, 16, 7, 7);
  block(s, C, 28, 9, 15, 6);
  block(s, C, 42, 14, 9); block(s, C, 56, 6, 6);
  block(s, C, 68, 10, 13);
  block(s, C, 84, 8, 9); block(s, C, 92, 11, 14, 8);
  block(s, C, 110, 7, 9); block(s, C, 117, 22, 6);
  block(s, C, 144, 12, 11); block(s, C, 156, 8, 8);
  block(s, C, 168, 10, 6);
}

// Wind turbine; the three blades swap between two poses on `f` so they read as turning.
const BLADES = [
  [[0, -1], [-0.87, 0.5], [0.87, 0.5]],
  [[0, 1], [-0.87, -0.5], [0.87, -0.5]],
];
function turbine(s: SceneCtx, C: Palette, x: number, h: number, len: number, f: number) {
  const { px, groundY: GY } = s; const hy = GY - h;
  px(x, hy, 1, h, C.turbine);
  for (const [dx, dy] of BLADES[f % 2]) for (let i = 1; i <= len; i++) px(x + Math.round(dx * i), hy + Math.round(dy * i), 1, 1, C.turbine);
  px(x - 1, hy - 1, 2, 2, C.turbine);
}

// Solar streetlight: a small blue panel on top, the lamp flickering on `f`.
function streetlight(s: SceneCtx, C: Palette, x: number, f: number) {
  const { px, glow, groundY: GY } = s;
  px(x, GY - 14, 1, 14, C.post);
  px(x - 2, GY - 16, 3, 1, C.panelHi); px(x - 2, GY - 15, 5, 1, C.panel);
  px(x + 1, GY - 12, 2, 1, C.post);
  px(x + 2, GY - 11, 2, 1, f % 2 ? C.lamp : C.lampLo);
  glow(x, GY - 10, 6, 10, C.lamp, 0.05);
}

// Solar greenhouse: a glass dome with copper ribs on a low brick base, plants inside.
function greenhouse(s: SceneCtx, C: Palette, green: boolean) {
  const { px, glow, groundY: GY, W } = s;
  const cx = W - 21, R = 13, DH = 15, base = GY - 3, top = base - DH;
  if (green) glow(cx - R - 6, top - 3, 2 * R + 13, GY - top + 6, C.green, 0.15);
  const glass = green ? mix(C.glass, C.green, 0.45) : C.glass;
  const rib = green ? mix(C.gild, C.green, 0.35) : C.gild;
  const leaf = mix(C.leaf, glass, 0.3);
  const half: number[] = [];
  for (let i = 0; i < DH; i++) {
    const v = (DH - i - 0.5) / DH, hw = Math.round(R * Math.sqrt(1 - v * v));
    half.push(hw);
    px(cx - hw, top + i, 2 * hw + 1, 1, glass);
  }
  // plants seen through the glass: a leafy tree, a sunflower, low beds
  px(cx - 5, base - 6, 1, 6, C.trunk); px(cx - 8, base - 11, 7, 5, leaf); px(cx - 7, base - 12, 5, 1, leaf);
  px(cx + 6, base - 9, 1, 9, leaf); px(cx + 7, base - 6, 2, 1, leaf);
  px(cx + 5, base - 12, 3, 3, C.accent); px(cx + 6, base - 11, 1, 1, C.seed);
  px(cx - 11, base - 2, 22, 2, leaf);
  px(cx - 9, base - 9, 2, 1, C.glassHi); px(cx - 10, base - 8, 1, 2, C.glassHi); // reflection
  // copper ribs: outline, three meridians, one latitude ring, a finial
  for (let i = 0; i < DH; i++) {
    const hw = half[i], y = top + i, step = Math.max(1, hw - (i ? half[i - 1] : 0));
    px(cx - hw, y, step, 1, rib); px(cx + hw - step + 1, y, step, 1, rib);
    const m2 = Math.round(hw * 0.55);
    px(cx, y, 1, 1, rib); px(cx - m2, y, 1, 1, rib); px(cx + m2, y, 1, 1, rib);
  }
  px(cx - half[8], top + 8, 2 * half[8] + 1, 1, rib);
  px(cx, top - 2, 1, 2, rib);
  px(cx - R - 1, base, 2 * R + 3, 3, C.brick); px(cx - R - 1, base, 2 * R + 3, 1, rib);
}

// A butterfly flitting a small loop; wings beat on `flap`.
const FLIT = [[0, 0], [2, -1], [3, 1], [1, 2], [-1, 1], [-2, -1]];
function butterfly(s: SceneCtx, C: Palette, x: number, y: number, t: number, flap: number, wing: string) {
  const { px } = s; const [dx, dy] = FLIT[t % FLIT.length], bx = x + dx, by = y + dy;
  if (flap) { px(bx - 2, by, 2, 1, wing); px(bx + 1, by, 2, 1, wing); px(bx - 1, by + 1, 1, 1, wing); px(bx + 1, by + 1, 1, 1, wing); }
  else { px(bx - 1, by - 2, 1, 2, wing); px(bx + 1, by - 2, 1, 2, wing); }
  px(bx, by - 1, 1, 3, C.bug);
}

// Pollen motes drifting up and across; a thicker drift once the panels are charged.
function pollen(s: SceneCtx, C: Palette, f: number, count: number) {
  const { px, W, groundY: GY } = s;
  for (let i = 0; i < count; i++) {
    const x = ((i * 47 + f * (1 + (i % 3))) % (W + 4)) - 2;
    const y = GY - 3 - ((i * 13 + f) % (GY - 8));
    px(x, y, 1, 1, i % 3 ? C.pollen : C.pollen2);
  }
}

// 7px spoked wheel; the spokes swap between + and x on `f`.
function wheel(s: SceneCtx, C: Palette, cx: number, cy: number, f: number) {
  const { px } = s;
  px(cx - 1, cy - 3, 3, 1, C.tyre); px(cx - 1, cy + 3, 3, 1, C.tyre);
  px(cx - 3, cy - 1, 1, 3, C.tyre); px(cx + 3, cy - 1, 1, 3, C.tyre);
  for (const [dx, dy] of [[-2, -2], [2, -2], [-2, 2], [2, 2]]) px(cx + dx, cy + dy, 1, 1, C.tyre);
  if (f) for (const [dx, dy] of [[-1, -1], [1, -1], [-1, 1], [1, 1]]) px(cx + dx, cy + dy, 1, 1, C.spoke);
  else { px(cx, cy - 2, 1, 5, C.spoke); px(cx - 2, cy, 5, 1, C.spoke); }
  px(cx, cy, 1, 1, C.crank);
}
// Cargo-bike cyclist in a sun hat riding right, a potted sprout in the front crate.
// `stopped` plants a foot and holds the wheels still; otherwise wheels and cranks turn.
function cyclist(s: SceneCtx, C: Palette, x: number, walkF: number, stopped: boolean) {
  const { px, groundY: GY } = s;
  const p = (dx: number, dy: number, w: number, h: number, c: string) => px(x + dx, GY + dy, w, h, c);
  const f = stopped ? 0 : walkF % 2, farUp = stopped || f === 1;
  // far leg, behind the bike, on the back pedal
  if (farUp) { p(6, -7, 1, 1, C.denimLo); p(7, -6, 1, 1, C.denimLo); p(6, -5, 1, 1, C.denimLo); p(5, -4, 2, 1, C.shoe); }
  else { p(6, -7, 1, 7, C.denimLo); p(6, 0, 2, 1, C.shoe); }
  wheel(s, C, x + 3, GY - 2, f); wheel(s, C, x + 12, GY - 2, f);
  // frame: chainstay, seat tube, top tube, down tube, seat stay, head tube + fork
  p(4, -2, 3, 1, C.frame); p(6, -6, 1, 3, C.frame); p(7, -3, 1, 1, C.frame);
  p(7, -6, 4, 1, C.frame); p(8, -3, 1, 1, C.frame); p(9, -4, 1, 1, C.frame); p(10, -5, 1, 1, C.frame);
  p(4, -3, 1, 1, C.frame); p(5, -5, 1, 2, C.frame);
  p(11, -7, 1, 2, C.frame); p(12, -5, 1, 3, C.frame);
  // handlebar, bottom bracket, and crank arms that flip diagonal as the pedals turn
  p(10, -8, 2, 1, C.crank); p(7, -2, 1, 1, C.crank);
  if (farUp) { p(6, -3, 1, 1, C.crank); p(8, -1, 1, 1, C.crank); } else { p(8, -3, 1, 1, C.crank); p(6, -1, 1, 1, C.crank); }
  // front crate with a potted sprout
  p(12, -8, 4, 3, C.crate); p(12, -8, 4, 1, mix(C.crate, '#ffffff', 0.2)); p(13, -7, 1, 1, C.crateLo);
  p(13, -10, 2, 2, C.pot); p(13, -10, 2, 1, C.soil);
  p(13, -11, 1, 1, C.leaf); p(12, -12, 1, 1, C.leaf); p(14, -12, 1, 1, C.leafHi);
  // rider: saddle, seat, shirt, arm to the bars, face under a wide sun hat
  p(5, -7, 3, 1, C.saddle); p(5, -8, 2, 1, C.denim);
  p(6, -11, 3, 3, C.shirt); p(6, -11, 3, 1, C.shirtHi);
  p(9, -10, 1, 1, C.shirt); p(10, -9, 1, 1, C.skin);
  p(6, -13, 1, 2, C.hair); p(7, -13, 4, 2, C.skin); p(9, -13, 1, 1, C.eye);
  p(5, -14, 8, 1, C.hat); p(7, -15, 4, 1, C.hatHi); p(7, -14, 4, 1, C.green);
  // near leg, in front of the frame
  if (stopped) { p(7, -7, 1, 2, C.denim); p(8, -5, 1, 6, C.denim); p(8, 1, 2, 1, C.shoe); }
  else if (f) { p(7, -7, 1, 2, C.denim); p(8, -5, 1, 5, C.denim); p(8, 0, 2, 1, C.shoe); }
  else { p(7, -8, 2, 1, C.denim); p(9, -7, 1, 3, C.denim); p(8, -4, 2, 1, C.shoe); }
}

// Smog cloud squatting on the greenway: rim-lit sooty puffs in a haze, two dim red
// eyes, soot specks drifting up. The crown billows on `f`.
function smog(s: SceneCtx, C: Palette, x: number, f: number) {
  const { px, groundY: GY } = s;
  disc(s, x + 9, GY - 7, 10, C.soot, 0.14);
  disc(s, x + 4, GY - 2, 3, C.sootLo); disc(s, x + 9, GY - 3, 4, C.sootLo); disc(s, x + 14, GY - 2, 3, C.sootLo);
  const puffs = [[x + 5, GY - 8, 3], [x + 13, GY - 8, 3], [x + 9, GY - 11 - f, 3], [x + 3, GY - 11, 2], [x + 14, GY - 13 + f, 2]];
  for (const [cx, cy, r] of puffs) disc(s, cx - 1, cy - 1, r, C.sootHi);
  for (const [cx, cy, r] of puffs) disc(s, cx, cy, r, C.soot);
  px(x + 6, GY - 7, 2, 1, C.smogEye); px(x + 11, GY - 7, 2, 1, C.smogEye);
  for (const [dx, dy] of [[-2, -12], [18, -15], [8, -18], [18, -5], [-1, -5]]) px(x + dx, GY + dy - f, 1, 1, C.speck);
}

const draw = (C: Palette, s: SceneCtx) => {
  const { px, mix: m, W, H, groundY: GY, pathL: PL, state: st, heroX: hx, walkF, flameF } = s;
  const tint = st === 'failure' ? C.red : st === 'success' ? C.green : C.accent;
  const allDone = st === 'success';
  // dusk sky, low sun, garden city, wind turbines
  for (let y = 0; y < GY; y++) px(0, y, W, 1, m(C.skyTop, C.skyBot, y / GY));
  sun(s, C);
  skyline(s, C);
  turbine(s, C, 56, 24, 6, walkF); turbine(s, C, 158, 20, 5, walkF + 1);
  // ground
  for (let y = GY; y < H; y++) px(0, y, W, 1, m(C.ground, C.groundLo, (y - GY) / (H - GY)));
  px(0, GY - 1, W, 2, C.edge);
  // greenway lane dashes + the trodden trail behind the bike
  const progX = st === 'idle' ? PL : hx + 6;
  for (let x = PL; x < W - 8; x += 10) { const done = allDone || x < progX; px(x, GY + 3, 6, 2, done ? tint : C.dash); }
  px(PL, GY + 2, allDone ? W - 8 - PL : Math.max(0, progX - PL), 1, m(tint, C.ground, 0.3));
  streetlight(s, C, 30, flameF); streetlight(s, C, 84, flameF + 1); streetlight(s, C, 138, flameF);
  greenhouse(s, C, allDone);
  pollen(s, C, walkF + flameF, allDone ? 16 : 6);
  if (st === 'idle') { butterfly(s, C, 180, 14, walkF, flameF, C.violet); butterfly(s, C, 208, 11, walkF + 3, (flameF + 1) % 2, C.amber); }
  cyclist(s, C, hx, walkF, st === 'idle' || st === 'failure');
  if (st === 'failure') smog(s, C, hx + 17, flameF);
};

export const solarpunk: SkinDef = {
  label: 'Solarpunk',
  PromoteScene: makeScene({ colors, draw }),
  lines: {
    pending: "Pedal on. The sun's with us.",
    failure: 'Smog on the greenway. Hold up.',
    success: "Panels charged. We're live. 🌻",
  },
};
